'use client';

import React, { useState, useEffect, useRef } from 'react';


// ─── Types ───────────────────────────────────────────────────────────────────
interface CartItem {
  id: string;
  name: string;
  qty: string;
  dose: string;
  price: number;
  emoji: string;
  flavor?: string;
}

interface PriceOption {
  qty: string;
  dose: string;
  price: number;
}

interface ProductLevel {
  label: string;
  options: PriceOption[];
  image?: string;
  texture?: string;
  taste?: string;
  strain?: string;
}

interface Product {
  id: string;
  emoji: string;
  label: string;
  title: string;
  subtitle: string;
  desc: string;
  color: 'purple' | 'gold' | 'red';
  levels: ProductLevel[];
  flavors: string[];
}

// ─── Data ────────────────────────────────────────────────────────────────────
const PRODUCTS: Product[] = [
{
  id: 'gummies',
  emoji: '/assets/gummy-bear.svg',
  label: 'THC Candies',
  title: 'Choose Your Variation',
  subtitle: 'Delicious Candies 🤤 Infused with RSO',
  desc: '',
  color: 'purple',
  levels: [
  {
    label: 'RSO Jelly Fruit Candies - 50mg Strong',
    image: "/assets/images/unnamed__1_-1784040540470.jpg",
    texture: 'Satisfying, firm bite. Unlike gelatin, which is chewy and bouncy, agar creates a gel that cuts cleanly when you bite into it. It dissolves smoothly and cleanly on the tongue without leaving a sticky or rubbery residue.',
    taste: 'Fruit-forward with an earthy subtext. A vibrant explosion of real fruit flavor is followed by a deep, herbal, and richly botanical RSO finish that lets you know you are getting the full medicinal benefits of the plant.',
    strain: 'Sour Diesel',
    options: [
    { qty: '10 Candies', dose: '50mg each · 500mg total', price: 1500 },
    { qty: '20 Candies', dose: '50mg each · 1000mg total', price: 2900 }]
  },
  {
    label: 'RSO Jelly Fruit Candies - 50mg Strong',
    image: "/assets/images/unnamed__1_-1784040540470.jpg",
    texture: 'Satisfying, firm bite. Unlike gelatin, which is chewy and bouncy, agar creates a gel that cuts cleanly when you bite into it. It dissolves smoothly and cleanly on the tongue without leaving a sticky or rubbery residue.',
    taste: 'Fruit-forward with an earthy subtext. A vibrant explosion of real fruit flavor is followed by a deep, herbal, and richly botanical RSO finish that lets you know you are getting the full medicinal benefits of the plant.',
    strain: 'Durban',
    options: [
    { qty: '10 Candies', dose: '50mg each · 500mg total', price: 1500 },
    { qty: '20 Candies', dose: '50mg each · 1000mg total', price: 2900 }]
  }],

  flavors: ['🍊 Orange Nectar', '🍓 Strawberry Sensation', '🍍 Pineapple Paradise']
},
{
  id: 'cookies',
  emoji: '/assets/medicine-bottle-oil.svg',
  label: 'Bangi Oils',
  title: 'Choose Your Bangi Oil',
  subtitle: 'BANGI Oils',
  desc: '',
  color: 'gold',
  levels: [
  {
    label: 'Nutty Bliss',
    image: 'https://d2j6dbq0eux0bg.cloudfront.net/images/111719874/5158430168.jpg',
    options: [
    { qty: '30ml', dose: '30mg THC per teaspoon · Carrier oil: Macadamia oil', price: 1500 }]

  },
  {
    label: 'RSO · Medium Potency',
    image: 'https://d2j6dbq0eux0bg.cloudfront.net/images/111719874/5197023430.jpg',
    options: [
    { qty: '10ml', dose: '3mg THC per drop · Carrier oil: MCT oil', price: 1700 }]

  },
  {
    label: 'RSO · Strong Potency',
    image: 'https://d2j6dbq0eux0bg.cloudfront.net/images/111719874/5197023430.jpg',
    options: [
    { qty: '10ml', dose: '10mg THC per drop · Carrier oil: MCT oil', price: 4700 }]

  }],

  flavors: []
}];


const REVIEWS = [
{ img: "/assets/images/unnamed-1779901974480.jpg", alt: 'Real customer review screenshot 1' },
{ img: "/assets/images/23476-1779901974220.jpg", alt: 'Real customer review screenshot 2' },
{ img: "/assets/images/27877-1779901974467.jpg", alt: 'Real customer review screenshot 3' }];



const NAIROBI_ZONES = [
'CBD', 'Westlands', 'Kilimani', 'Lavington', 'Karen', 'Langata',
'South B', 'South C', 'Eastleigh', 'Kasarani', 'Roysambu', 'Ruaka',
'Kileleshwa', 'Parklands', 'Runda', 'Gigiri', 'Muthaiga', 'Syokimau', 'Rongai', 'Other'];


// ─── Colour helpers ──────────────────────────────────────────────────────────
const colorMap = {
  purple: {
    label: 'lbl-p',
    title: 'c-purple',
    actTab: 'act-p',
    pp: 'pp-p',
    addBtn: 'ab-p',
    fchip: 'fchip-p',
    cardBorder: 'border-[rgba(124,58,237,0.20)]',
    cardHover: 'hover:shadow-[0_8px_50px_rgba(124,58,237,0.15)]'
  },
  gold: {
    label: 'lbl-g',
    title: 'c-gold',
    actTab: 'act-g',
    pp: 'pp-g',
    addBtn: 'ab-g',
    fchip: 'fchip-g',
    cardBorder: 'border-[rgba(212,160,23,0.20)]',
    cardHover: 'hover:shadow-[0_8px_50px_rgba(212,160,23,0.12)]'
  },
  red: {
    label: 'lbl-r',
    title: 'c-red',
    actTab: 'act-r',
    pp: 'pp-r',
    addBtn: 'ab-r',
    fchip: 'fchip-p',
    cardBorder: 'border-[rgba(192,57,43,0.20)]',
    cardHover: 'hover:shadow-[0_8px_50px_rgba(192,57,43,0.12)]'
  }
};

const ppColors: Record<string, string> = {
  'pp-p': 'text-[#D4600A]',
  'pp-g': 'text-[#E8A820]',
  'pp-r': 'text-[#B04A00]'
};

// ─── Sub-components ──────────────────────────────────────────────────────────
function SectionHeader({ emoji, title, sub, colorClass }: {emoji: string;title: string;sub: string;colorClass: string;}) {
  return (
    <div className="text-center mb-14">
      {emoji.startsWith('/') ?
      <span className="block mb-2 animate-pulse-icon flex justify-center">
          <img src={emoji} alt={sub} className="w-12 h-12 mx-auto" />
        </span> :

      <span className="text-5xl block mb-2 animate-pulse-icon">{emoji}</span>
      }
      <div className={`font-bebas text-[clamp(36px,8vw,80px)] tracking-[3px] ${colorClass}`}>{title}</div>
      <p className="text-text-muted text-[13px] tracking-[1.5px] mt-1">{sub}</p>
    </div>);

}

function ProductCard({ product, onAddToCart }: {product: Product;onAddToCart: (item: CartItem) => void;}) {
  const [activeLevel, setActiveLevel] = useState(0);
  const [selectedFlavor, setSelectedFlavor] = useState<string | null>(null);
  const [flavorWarn, setFlavorWarn] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedStrain, setSelectedStrain] = useState<'Sour Diesel' | 'Durban'>('Sour Diesel');
  const c = colorMap[product.color];

  const isBangiOils = product.id === 'cookies';
  const strainPriceAddon = 0;

  const handleAdd = (opt: PriceOption) => {
    const isVapingLiquidStrong = product.id === 'brownies' && activeLevel === 1;
    if (product.flavors.length > 0 && !selectedFlavor && !isVapingLiquidStrong) {
      setFlavorWarn(true);
      return;
    }
    setFlavorWarn(false);
    const finalPrice = opt.price + strainPriceAddon;
    const strainSuffix = isBangiOils ? ` (${selectedStrain})` : '';
    const isGummies = product.id === 'gummies';
    const isVapingLiquid = product.id === 'brownies';
    const levelStrain = product.levels[activeLevel].strain;
    const cartLevelLabel = isGummies && levelStrain
      ? `RSO Jelly Fruit Candies - 50mg (${levelStrain})`
      : product.levels[activeLevel].label;
    const cartName = isVapingLiquid
      ? `${product.label}${strainSuffix}`
      : `${product.label}: ${cartLevelLabel}${strainSuffix}`;
    onAddToCart({
      id: `${product.id}-${activeLevel}-${opt.qty}-${isBangiOils ? selectedStrain : (product.levels[activeLevel].strain ?? '')}`,
      name: cartName,
      qty: opt.qty,
      dose: opt.dose,
      price: finalPrice,
      emoji: product.emoji,
      flavor: isVapingLiquidStrong ? undefined : (selectedFlavor ?? undefined)
    });
  };

  return (
    <div className={`bg-card border ${c.cardBorder} rounded-[20px] overflow-hidden shadow-[0_4px_40px_rgba(0,0,0,0.40)] mb-8 transition-shadow duration-300 ${c.cardHover}`}>
      <div className="card-body p-5 sm:p-8 pb-6 sm:pb-9">
        {/* Label */}
        <div className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 mb-4 text-[10px] tracking-[2.5px] uppercase font-medium ${
        product.color === 'purple' ? 'bg-[rgba(139,58,0,0.15)] border border-[rgba(139,58,0,0.30)] text-[#D4600A]' :
        product.color === 'gold' ? 'bg-[rgba(200,134,10,0.12)] border border-[rgba(200,134,10,0.30)] text-[#E8A820]' :
        'bg-[rgba(212,96,10,0.12)] border border-[rgba(212,96,10,0.30)] text-[#D4600A]'}`
        }>
          {product.emoji.startsWith('/') ?
          <>
              <img src={product.emoji} alt={product.label} className="w-4 h-4" />
              {product.label}
            </> :

          <>
              {product.emoji} {product.label}
            </>
          }
        </div>

        {/* Title */}
        <div className={`font-bebas text-[clamp(22px,4vw,32px)] tracking-[2px] mb-1 ${c.title}`}>{product.title}</div>

        {/* Level tabs — shown before description for Infused Vaping Liquid */}
        {product.id === 'brownies' &&
        <div className="flex flex-wrap gap-2 mb-4 mt-2">
            {product.levels.length > 1 && product.levels.map((lv, i) =>
          <button
            key={i}
            className={`lvl-tab ${activeLevel === i ? c.actTab : ''}`}
            onClick={() => setActiveLevel(i)}>
              
                {lv.label}
                {lv.label === 'Low Potency' && (
                  <span className="inline-block ml-1.5 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-[1.5px] uppercase" style={{ background: 'rgba(34,197,94,0.18)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.40)' }}>BUDGET</span>
                )}
                {lv.label === 'Strong Potency' && (
                  <span className="inline-block ml-1.5 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-[1.5px] uppercase" style={{ background: 'rgba(200,134,10,0.22)', color: '#f59e0b', border: '1px solid rgba(200,134,10,0.50)' }}>PREMIUM</span>
                )}
              </button>
          )}
          </div>
        }

        {/* Description */}
        {product.desc &&
        <p className="text-text-muted text-[12px] leading-relaxed tracking-[0.5px] mb-4 whitespace-pre-line">{product.desc}</p>
        }

        {/* Level tabs — shown after description for all other products */}
        {product.id !== 'brownies' &&
        <div className="flex flex-wrap gap-2 mb-5">
            {product.levels.map((lv, i) =>
          <button
            key={i}
            className={`lvl-tab ${activeLevel === i ? c.actTab : ''}`}
            onClick={() => setActiveLevel(i)}>
              {product.id === 'gummies' && lv.strain === 'Sour Diesel' && (
                <span className="inline-block mr-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-[1px] uppercase" style={{ background: 'rgba(34,197,94,0.18)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.40)' }}>Sour Diesel</span>
              )}
              {product.id === 'gummies' && lv.strain === 'Durban' && (
                <span className="inline-block mr-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-[1px] uppercase" style={{ background: 'rgba(139,42,170,0.18)', color: '#c084fc', border: '1px solid rgba(139,42,170,0.45)' }}>Durban</span>
              )}
                {lv.label}
              </button>
          )}
          </div>
        }

        {/* Level product image — all images rendered at once so browser loads them all immediately */}
        {product.levels.some((lv) => lv.image) &&
        <div className="mb-5 flex justify-center flex-col items-center">
            <div className="relative overflow-hidden rounded-xl cursor-pointer" onClick={() => setLightboxOpen(true)} title="Click to expand" style={{ background: '#221408' }}>
              {product.levels.map((lv, idx) =>
                lv.image ? (
                  <img
                    key={lv.label}
                    src={lv.image}
                    alt={`${lv.label} product`}
                    loading="eager"
                    decoding="sync"
                    style={{ display: idx === activeLevel ? 'block' : 'none' }}
                    className="rounded-xl object-contain w-full max-w-xs h-64 shadow-lg border border-[rgba(212,160,23,0.25)] hover:scale-110 transition-transform duration-200 ease-in-out"
                  />
                ) : null
              )}
              {product.levels[activeLevel].label.toLowerCase().includes('strong potency') &&
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold tracking-[2px] uppercase whitespace-nowrap pointer-events-none"
            style={{ background: 'rgba(200,134,10,0.85)', color: '#1A0E06', border: '1px solid rgba(232,168,32,0.6)', boxShadow: '0 2px 8px rgba(200,134,10,0.4)' }}>
                  Strong Potency
                </div>
            }
            </div>
          </div>
        }

        {/* Texture & Taste info */}
        {(product.levels[activeLevel].texture || product.levels[activeLevel].taste) &&
        <div className="mb-5 grid gap-2.5 sm:grid-cols-2">
            {product.levels[activeLevel].texture &&
          <div className="rounded-xl p-4" style={{ background: 'rgba(139,42,170,0.10)', border: '1px solid rgba(139,42,170,0.22)' }}>
                <div className="text-[9px] tracking-[2.5px] uppercase font-semibold mb-1.5" style={{ color: '#B06AD4' }}>✦ Texture</div>
                <p className="text-[11.5px] leading-relaxed text-text-muted">{product.levels[activeLevel].texture}</p>
              </div>
          }
            {product.levels[activeLevel].taste &&
          <div className="rounded-xl p-4" style={{ background: 'rgba(212,96,10,0.08)', border: '1px solid rgba(212,96,10,0.20)' }}>
                <div className="text-[9px] tracking-[2.5px] uppercase font-semibold mb-1.5" style={{ color: '#D4600A' }}>✦ Taste</div>
                <p className="text-[11.5px] leading-relaxed text-text-muted">{product.levels[activeLevel].taste}</p>
              </div>
          }
          </div>
        }

        {/* Strain info */}
        {product.levels[activeLevel].strain &&
        <div className="mb-5 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] tracking-[1px] font-semibold" style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.28)', color: '#4ade80' }}>
          <span className="text-[9px] tracking-[2px] uppercase opacity-70">Strain</span>
          <span>{product.levels[activeLevel].strain}</span>
        </div>
        }

        {/* Lightbox */}
        {lightboxOpen && product.levels[activeLevel].image &&
        <div
          className="fixed inset-0 z-[500] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.92)' }}
          onClick={() => setLightboxOpen(false)}>
          
            <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              <button
              onClick={() => setLightboxOpen(false)}
              className="absolute -top-4 -right-4 w-9 h-9 rounded-full flex items-center justify-center text-white text-lg font-bold z-10 transition-colors"
              style={{ background: 'rgba(212,160,23,0.25)', border: '1px solid rgba(212,160,23,0.40)' }}>
              
                ✕
              </button>
              <img
              src={product.levels[activeLevel].image}
              alt={`${product.levels[activeLevel].label} product expanded`}
              className="rounded-2xl object-contain shadow-2xl"
              style={{ maxWidth: '90vw', maxHeight: '85vh' }} />
            
            </div>
          </div>
        }

        {/* Flavors */}
        {product.flavors.length > 0 && !(product.id === 'brownies' && activeLevel === 1) &&
        <div className="mt-6 pt-5 border-t border-[rgba(255,255,255,0.07)]">
            <div className="flex items-center gap-2.5 text-[10px] tracking-[3px] uppercase text-text-muted font-medium mb-3.5">
              Select Your {product.label} Flavour
              <span className="flex-1 h-px bg-[rgba(255,255,255,0.07)]" />
            </div>
            <div className="flex flex-wrap gap-2">
              {product.flavors.map((f) =>
            <button
              key={f}
              className={`fchip ${c.fchip} ${selectedFlavor === f ? 'sel' : ''}`}
              onClick={() => {setSelectedFlavor(f);setFlavorWarn(false);}}>
                {f === '🍓 Strawberry Donut' ? (
                  <span className="inline-flex items-center gap-1.5">
                    <img src="/assets/strawberry-donut-icon.svg" alt="strawberry donut" className="w-4 h-4 inline-block" />
                    Strawberry Donut
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-[1px] uppercase" style={{ background: 'rgba(139,42,170,0.18)', color: '#c084fc', border: '1px solid rgba(139,42,170,0.40)' }}>Indica</span>
                  </span>
                ) : f}
                </button>
            )}
            </div>
            {flavorWarn &&
          <p className="text-[11px] text-[#D4600A] tracking-[1px] mt-2 font-semibold">
                ⚠️ Please select a flavour before adding to cart
              </p>
          }
          </div>
        }

        {/* Strain selector — Bangi Oils only */}
        {isBangiOils &&
        <div className="mt-5 mb-5 pt-5 border-t border-[rgba(255,255,255,0.07)]">
          <div className="flex items-center gap-2.5 text-[10px] tracking-[3px] uppercase text-text-muted font-medium mb-3">
            Select Strain
            <span className="flex-1 h-px bg-[rgba(255,255,255,0.07)]" />
          </div>
          <div className="flex gap-2">
                        {(['Sour Diesel', 'Durban'] as const).map((strain) =>
              <button
                key={strain}
                onClick={() => setSelectedStrain(strain)}
                className={`px-4 py-2 rounded-full text-[11px] font-semibold tracking-[1.5px] uppercase transition-all border ${
                  selectedStrain === strain
                    ? 'bg-[rgba(200,134,10,0.22)] border-[rgba(232,168,32,0.65)] text-[#E8A820] shadow-[0_0_10px_rgba(200,134,10,0.25)]'
                    : 'bg-transparent border-[rgba(200,134,10,0.20)] text-text-muted hover:border-[rgba(232,168,32,0.40)] hover:text-[#E8A820]'
                }`}>
                {strain}
              </button>
            )}
          </div>
        </div>
        }

        {/* Price grid */}
        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(152px, 1fr))' }}>
          {product.levels[activeLevel].options.map((opt, i) =>
          <div key={i} className="price-opt">
              <span className="font-semibold text-[15px] text-text block mb-0.5">{opt.qty}</span>
              <span className="text-[10px] text-text-muted tracking-[0.8px] block mb-2.5">{opt.dose}</span>
              <span className={`font-bold text-[18px] block mb-2.5 ${ppColors[c.pp]}`}>KSh {(opt.price + strainPriceAddon).toLocaleString()}</span>
              <button className={`add-btn ${c.addBtn}`} onClick={() => handleAdd(opt)}>+ ADD TO CART</button>
            </div>
          )}
        </div>
      </div>
    </div>);

}

function CartDrawer({
  open, items, onClose, onRemove, selectedZone




}: {open: boolean;items: CartItem[];onClose: () => void;onRemove: (id: string) => void;selectedZone: string | null;}) {
  const total = items.reduce((s, i) => s + i.price, 0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handleCheckout = () => {
    if (items.length === 0) return;
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = () => {
    const lines = items.map((i) => `${i.name} — ${i.qty}${i.flavor ? ` (${i.flavor})` : ''} — KSh ${i.price.toLocaleString()}`).join('%0A');
    const zoneText = selectedZone ? `%0ADelivery Zone: ${selectedZone}` : '';
    const msg = `Hello! I'd like to order:%0A${lines}%0A%0ATotal: KSh ${total.toLocaleString()}${zoneText}%0A%0AI have paid via M-Pesa Paybill 501101, Account 000079996.`;
    window.open(`https://wa.me/254785139571?text=${msg}`, '_blank');
    setShowPaymentModal(false);
  };

  const handleCancelPayment = () => {
    setShowPaymentModal(false);
  };

  return (
    <>
      <div className={`cart-overlay ${open ? 'open' : ''}`} onClick={onClose} />
      <div className={`cart-drawer ${open ? 'open' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b-2 border-[#C8860A]" style={{ background: 'linear-gradient(135deg, #5C2500, #8B3A00)' }}>
          <span className="font-bebas text-xl tracking-[3px] text-[#FFF3E0]">🛒 YOUR ORDER</span>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[rgba(255,255,255,0.10)] text-white text-lg flex items-center justify-center hover:bg-[rgba(255,255,255,0.25)] transition-colors">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Selected delivery zone */}
          {selectedZone && (
            <div className="mb-4 flex items-center gap-2.5 px-4 py-3 rounded-xl text-[12px] font-medium" style={{ background: 'rgba(200,134,10,0.10)', border: '1px solid rgba(200,134,10,0.30)', color: '#E8A820' }}>
              <span className="text-base">📍</span>
              <div>
                <div className="text-[9px] tracking-[2px] uppercase text-text-muted mb-0.5">Delivery Zone</div>
                <div className="font-semibold tracking-[0.5px]">{selectedZone}</div>
              </div>
            </div>
          )}
          {items.length === 0 ?
          <div className="text-center py-12 text-text-muted">
              <span className="text-5xl block mb-4 opacity-50">🛒</span>
              <p>Your cart is empty.</p>
              <p>Add items from the menu!</p>
            </div> :

          items.map((item) =>
          <div key={item.id} className="flex items-start gap-3 py-3 border-b border-[rgba(255,255,255,0.06)] animate-slide-in">
                <div className="flex-1">
                  <p className="font-semibold text-[13px] text-text mb-0.5">
                    {item.emoji.startsWith('/') ? <img src={item.emoji} alt="" className="inline w-4 h-4 mr-1" /> : item.emoji} {item.name}
                  </p>
                  <p className="text-[11px] text-text-muted">{item.qty} · {item.dose}</p>
                  {item.flavor && <p className="text-[11px] text-text-muted">{item.flavor === '🍓 Strawberry Donut' ? <span className="inline-flex items-center gap-1"><img src="/assets/strawberry-donut-icon.svg" alt="strawberry donut" className="w-3 h-3 inline-block" />Strawberry Donut<span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-[1px] uppercase" style={{ background: 'rgba(139,42,170,0.18)', color: '#c084fc', border: '1px solid rgba(139,42,170,0.40)' }}>Indica</span></span> : item.flavor}</p>}
                  <p className="font-bold text-[14px] mt-1 text-[#D4600A]">KSh {item.price.toLocaleString()}</p>
                </div>
                <button
              onClick={() => onRemove(item.id)}
              className="w-6 h-6 rounded-full bg-[rgba(212,96,10,0.15)] text-[#D4600A] text-sm flex items-center justify-center hover:bg-[#B04A00] hover:text-white transition-all mt-0.5 flex-shrink-0">
              ×</button>
              </div>
          )
          }
        </div>

        {/* Footer */}
        <div className="border-t border-[rgba(200,134,10,0.25)] px-6 py-5 bg-[rgba(26,14,6,0.40)]">
          <div className="flex justify-between items-center mb-4">
            <span className="text-[12px] tracking-[2px] uppercase text-text-muted">Total</span>
            <span className="font-bebas text-[28px] tracking-[1px] text-[#E8A820]">KSh {total.toLocaleString()}</span>
          </div>
          <button
            onClick={handleCheckout}
            className="w-full py-3.5 rounded-xl text-white text-[13px] tracking-[2px] font-semibold transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #6B3A1A, #4A2510)', boxShadow: '0 4px 20px rgba(107,58,26,0.45)' }}>
            <span className="flex items-center justify-center gap-2.5">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" className="w-6 h-6 flex-shrink-0">
                <path d="M16 0C7.163 0 0 7.163 0 16c0 2.833.742 5.494 2.043 7.8L.057 31.428a.75.75 0 0 0 .921.921l7.697-1.984A15.937 15.937 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 0 1-6.773-1.853l-.484-.29-5.03 1.297 1.33-4.868-.317-.503A13.27 13.27 0 0 1 2.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.274-9.878c-.406-.203-2.403-1.186-2.775-1.322-.373-.136-.644-.203-.915.203-.272.406-1.05 1.322-1.286 1.593-.237.271-.474.305-.88.102-.406-.203-1.716-.633-3.268-2.017-1.208-1.078-2.024-2.41-2.261-2.816-.237-.406-.025-.625.178-.828.183-.182.406-.474.61-.711.203-.237.271-.406.406-.677.136-.271.068-.508-.034-.711-.102-.203-.915-2.205-1.253-3.019-.33-.792-.666-.685-.915-.697-.237-.011-.508-.014-.779-.014-.271 0-.711.102-1.083.508-.373.406-1.422 1.389-1.422 3.389s1.456 3.932 1.659 4.203c.203.271 2.866 4.376 6.944 6.136.97.419 1.727.669 2.317.855.973.31 1.859.267 2.559.162.781-.116 2.403-.983 2.743-1.932.339-.949.339-1.762.237-1.932-.102-.169-.373-.271-.779-.474z" />
              </svg>
              ORDER VIA WHATSAPP
            </span>
          </button>
          <p className="text-[11px] text-[#D4600A] text-center mt-2.5 tracking-[1.5px] font-semibold uppercase">Full payment required on all orders</p>
        </div>
      </div>

      {/* Payment Instructions Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.80)' }}>
          <div className="w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl flex flex-col" style={{ background: 'linear-gradient(160deg, #1A0E06, #2A1200)', border: '1px solid rgba(200,134,10,0.35)', maxHeight: '90vh' }}>
            {/* Modal Header */}
            <div className="px-6 py-3 text-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #5C2500, #8B3A00)', borderBottom: '2px solid #C8860A' }}>
              <div className="text-2xl mb-0.5">💳</div>
              <h2 className="font-bebas text-xl tracking-[3px] text-[#FFF3E0]">MPESA PAYMENT INSTRUCTIONS</h2>
            </div>

            {/* Modal Body — scrollable */}
            <div className="px-4 py-3 overflow-y-auto flex-1 min-h-0">
              {/* Simple Instructions */}
              <p className="text-[12px] leading-relaxed text-text-muted mb-3 text-center">
                Please pay to the following Paybill. Once you paid, please submit the order.
              </p>

              {/* Payment Details */}
              <div className="rounded-xl p-3 mb-3 space-y-2" style={{ background: 'rgba(200,134,10,0.08)', border: '1px solid rgba(200,134,10,0.30)' }}>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] tracking-[2px] uppercase text-text-muted">Paybill Number</span>
                  <span className="font-bebas text-[22px] tracking-[2px] text-[#E8A820]">501101</span>
                </div>
                <div className="h-px bg-[rgba(200,134,10,0.20)]" />
                <div className="flex justify-between items-center">
                  <span className="text-[10px] tracking-[2px] uppercase text-text-muted">Account Number</span>
                  <span className="font-bebas text-[18px] tracking-[1.5px] text-[#E8A820]">000079996</span>
                </div>
                <div className="h-px bg-[rgba(200,134,10,0.20)]" />
                <div className="flex justify-between items-center">
                  <span className="text-[10px] tracking-[2px] uppercase text-text-muted">Amount to Pay</span>
                  <span className="font-bebas text-[22px] tracking-[2px] text-[#4ade80]">KSh {total.toLocaleString()}</span>
                </div>
              </div>

              {/* Delivery Note */}
              <div className="rounded-xl p-3 mb-2" style={{ background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.20)' }}>
                <p className="text-[11px] leading-relaxed text-text-muted">
                  <span className="text-[#4ade80] font-semibold">🛵 Delivery fee</span> is paid upon delivery to the rider. We will confirm the delivery cost on <span className="text-[#25D366] font-semibold">WhatsApp</span> after you place your order and pay for the products.
                </p>
              </div>
            </div>

            {/* Buttons — always visible at bottom */}
            <div className="px-4 py-3 flex-shrink-0" style={{ borderTop: '1px solid rgba(200,134,10,0.20)', background: 'linear-gradient(160deg, #1A0E06, #2A1200)' }}>
              <button
                onClick={handleConfirmPayment}
                className="w-full py-3 rounded-xl text-white text-[12px] tracking-[1.5px] font-semibold mb-2 transition-all hover:-translate-y-0.5 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)', boxShadow: '0 4px 20px rgba(22,163,74,0.40)' }}>
                <span className="flex items-center justify-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" className="w-5 h-5 flex-shrink-0">
                    <path d="M16 0C7.163 0 0 7.163 0 16c0 2.833.742 5.494 2.043 7.8L.057 31.428a.75.75 0 0 0 .921.921l7.697-1.984A15.937 15.937 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 0 1-6.773-1.853l-.484-.29-5.03 1.297 1.33-4.868-.317-.503A13.27 13.27 0 0 1 2.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.274-9.878c-.406-.203-2.403-1.186-2.775-1.322-.373-.136-.644-.203-.915.203-.272.406-1.05 1.322-1.286 1.593-.237.271-.474.305-.88.102-.406-.203-1.716-.633-3.268-2.017-1.208-1.078-2.024-2.41-2.261-2.816-.237-.406-.025-.625.178-.828.183-.182.406-.474.61-.711.203-.237.271-.406.406-.677.136-.271.068-.508-.034-.711-.102-.203-.915-2.205-1.253-3.019-.33-.792-.666-.685-.915-.697-.237-.011-.508-.014-.779-.014-.271 0-.711.102-1.083.508-.373.406-1.422 1.389-1.422 3.389s1.456 3.932 1.659 4.203c.203.271 2.866 4.376 6.944 6.136.97.419 1.727.669 2.317.855.973.31 1.859.267 2.559.162.781-.116 2.403-.983 2.743-1.932.339-.949.339-1.762.237-1.932-.102-.169-.373-.271-.779-.474z" />
                  </svg>
                  ✅ I PAID, SUBMIT MY ORDER
                </span>
              </button>

              <button
                onClick={handleCancelPayment}
                className="w-full py-2.5 rounded-xl text-[12px] tracking-[1.5px] font-semibold transition-all hover:bg-[rgba(255,255,255,0.08)] active:scale-95"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#9ca3af' }}>
                ✕ CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </>);

}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Home() {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [popupAlertDismissed, setPopupAlertDismissed] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef<HTMLIFrameElement>(null);

  // Fade-up observer
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add('vis');}),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.fade-up').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setToastVisible(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastVisible(false), 2800);
  };

  const addToCart = (item: CartItem) => {
    setCart((prev) => [...prev, { ...item, id: `${item.id}-${Date.now()}` }]);
    showToast(item.emoji.startsWith('/') ? `🛒 Added to cart!` : `${item.emoji} Added to cart!`);
  };

  const removeFromCart = (id: string) => setCart((prev) => prev.filter((i) => i.id !== id));

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="relative z-[1]">
      {/* ── Pop-up Blocker Alert Banner ── */}
      {!popupAlertDismissed && (
        <div
          className="relative flex items-start sm:items-center justify-between gap-3 px-4 py-3 text-sm font-medium"
          style={{ background: 'rgba(180,80,0,0.92)', borderBottom: '1px solid rgba(255,160,50,0.45)' }}
          role="alert"
        >
          <div className="flex items-start sm:items-center gap-2 flex-1">
            <span className="text-[18px] leading-none mt-0.5 sm:mt-0">⚠️</span>
            <p className="text-[#FFF3DC] leading-snug text-[12px] sm:text-[13px] tracking-wide">
              <span className="font-bold text-[#FFD580]">Please disable your pop-up blocker</span> for this website — payment information and order details are displayed in a pop-up window and will not be visible if pop-ups are blocked.
            </p>
          </div>
          <button
            onClick={() => setPopupAlertDismissed(true)}
            aria-label="Dismiss alert"
            className="shrink-0 text-[#FFD580] hover:text-white transition-colors text-[18px] leading-none font-bold ml-2"
          >
            ×
          </button>
        </div>
      )}

      {/* ── Header ── */}
      <header className="sticky top-0 z-[200] flex items-center justify-between px-4 sm:px-6 h-[62px]" style={{ background: 'rgba(14,8,24,0.92)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(212,160,23,0.25)' }}>
        <div className="font-bebas text-[clamp(18px,3.5vw,24px)] tracking-[3px] flex items-center gap-2 grad-purple-gold">
            Alchemy candy
          </div>
        <nav className="hidden md:flex gap-5 items-center">
          {['gummies', 'cookies', 'reviews', 'delivery'].map((s) =>
          <button key={s} onClick={() => scrollTo(s)} className="text-[11px] tracking-[2.5px] uppercase text-text-muted hover:text-[#E8A820] transition-colors font-medium">
              {s === 'cookies' ? 'bangi oils' : s === 'gummies' ? 'THC Candies' : s}
            </button>
          )}
          <a href="https://alchemycandy.live/forum" target="_blank" rel="noopener noreferrer" className="text-[11px] tracking-[2.5px] uppercase text-text-muted hover:text-[#E8A820] transition-colors font-medium">Forum</a>
        </nav>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCartOpen(true)}
            className="flex items-center gap-2 rounded-full px-3 sm:px-4 py-2 text-[12px] tracking-[1.5px] font-medium text-[#E8A820] transition-all hover:bg-[rgba(200,134,10,0.20)]"
            style={{ background: 'rgba(200,134,10,0.10)', border: '1px solid rgba(200,134,10,0.35)' }}>
            🛒 <span className="hidden sm:inline">Cart</span>
            <span className={`bg-[#8B3A00] text-white w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${cart.length > 0 ? 'animate-bump' : ''}`}>
              {cart.length}
            </span>
          </button>
          {/* Mobile hamburger */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-9 h-9 gap-[5px] rounded-lg transition-colors"
            style={{ background: 'rgba(14,8,24,0.97)', border: '1px solid rgba(212,160,23,0.20)' }}
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu">
            
            <span className={`block w-5 h-0.5 bg-[#E8A820] transition-transform duration-200 ${mobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
            <span className={`block w-5 h-0.5 bg-[#E8A820] transition-opacity duration-200 ${mobileMenuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-5 h-0.5 bg-[#E8A820] transition-transform duration-200 ${mobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
          </button>
        </div>
      </header>

      {/* ── Mobile Nav Dropdown ── */}
      {mobileMenuOpen &&
      <div className="md:hidden sticky top-[62px] z-[199] flex flex-col py-3 px-4 gap-1" style={{ background: 'rgba(14,8,24,0.97)', borderBottom: '1px solid rgba(212,160,23,0.20)', backdropFilter: 'blur(16px)' }}>
          {['gummies', 'cookies', 'reviews', 'delivery'].map((s) =>
        <button
          key={s}
          onClick={() => scrollTo(s)}
          className="text-left py-3 px-4 rounded-xl text-[12px] tracking-[2px] uppercase text-text-muted hover:text-[#E8A820] hover:bg-[rgba(200,134,10,0.07)] transition-all font-medium">
          
              {s === 'cookies' ? '🫙 Bangi Oils' : s === 'gummies' ? <span className="inline-flex items-center gap-1"><img src="/assets/gummy-bear.svg" alt="THC Candies" className="w-4 h-4 inline-block" /> THC Candies</span> : s === 'reviews' ? '💬 Reviews' : '📍📦 Delivery'}
            </button>
        )}
          <a
          href="https://alchemycandy.live/forum"
          target="_blank"
          rel="noopener noreferrer"
          className="text-left py-3 px-4 rounded-xl text-[12px] tracking-[2px] uppercase text-text-muted hover:text-[#E8A820] hover:bg-[rgba(200,134,10,0.07)] transition-all font-medium">
          
            💬 Forum
          </a>
        </div>
      }

      {/* ── Hero ── */}
      <section className="relative z-[1] min-h-[92vh] flex flex-col items-center justify-center text-center px-4 sm:px-6 py-16 overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 90% 70% at 50% 40%, rgba(107,26,138,0.18) 0%, transparent 65%), radial-gradient(ellipse 60% 40% at 20% 90%, rgba(26,107,60,0.12) 0%, transparent 60%)' }} />
        {/* Floating ornaments removed */}
        

        <div className="relative z-[1] w-full max-w-2xl mx-auto">
          {/* Hero image above badge */}
          <div className="flex justify-center mb-4">
            <img
              src="/assets/images/Untitled_design-1783520669277.png"
              alt="Alchemy Candy hero visual"
              className="w-72 sm:w-96 md:w-[28rem] object-contain"
              style={{ opacity: 0.92, mixBlendMode: 'luminosity', animation: 'heroFloat 3.5s ease-in-out infinite' }} />
            
          </div>

          {/* YouTube Music Player */}
          <div className="flex flex-col items-center gap-3 mb-6">
            <p className="font-playfair italic text-text-muted text-[13px] sm:text-[14px] leading-relaxed">
              New track: Bob Marley - Kaya 🎵 Press play to vibe while you browse ^_^
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsPlaying(true)}
                disabled={isPlaying}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-[12px] tracking-[1.5px] font-medium transition-all hover:-translate-y-0.5 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:scale-100"
                style={{ background: 'linear-gradient(135deg, #1a6b3c, #2a8a50)', color: '#d4f5e2', border: '1px solid rgba(42,138,80,0.45)', boxShadow: '0 4px 20px rgba(26,107,60,0.45)' }}>
                ▶ Play Song
              </button>
              <button
                onClick={() => setIsPlaying(false)}
                disabled={!isPlaying}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-[12px] tracking-[1.5px] font-medium transition-all hover:-translate-y-0.5 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:scale-100"
                style={{ background: 'rgba(107,26,138,0.20)', color: '#d4b8f0', border: '1px solid rgba(107,26,138,0.40)', boxShadow: '0 4px 20px rgba(107,26,138,0.25)' }}>
                ■ Stop Song
              </button>
            </div>

            {/* Chilling Bob Marley character */}
            {isPlaying && (
              <div className="flex flex-col items-center gap-1 mt-1">
                <style>{`
                  @keyframes bobChill {
                    0%   { transform: translateY(0px) rotate(-1.5deg); }
                    25%  { transform: translateY(-5px) rotate(1.5deg); }
                    50%  { transform: translateY(-2px) rotate(-1deg); }
                    75%  { transform: translateY(-6px) rotate(2deg); }
                    100% { transform: translateY(0px) rotate(-1.5deg); }
                  }
                  @keyframes bobHeadNod {
                    0%, 100% { transform: rotate(-4deg); }
                    50%       { transform: rotate(4deg); }
                  }
                  @keyframes bobArmLeft {
                    0%, 100% { transform: rotate(-10deg); }
                    50%       { transform: rotate(10deg); }
                  }
                  @keyframes bobArmRight {
                    0%, 100% { transform: rotate(10deg); }
                    50%       { transform: rotate(-10deg); }
                  }
                  @keyframes dreadFlow {
                    0%, 100% { transform: skewX(-3deg) scaleX(1); }
                    50%       { transform: skewX(3deg) scaleX(1.03); }
                  }
                  @keyframes smokeRise {
                    0%   { transform: translate(0,0) scale(1); opacity: 0.7; }
                    100% { transform: translate(4px,-20px) scale(1.5); opacity: 0; }
                  }
                  @keyframes noteFloat1 {
                    0%   { transform: translate(0,0) rotate(0deg); opacity: 0.9; }
                    100% { transform: translate(8px,-18px) rotate(20deg); opacity: 0; }
                  }
                  @keyframes noteFloat2 {
                    0%   { transform: translate(0,0) rotate(0deg); opacity: 0.8; }
                    100% { transform: translate(-8px,-16px) rotate(-15deg); opacity: 0; }
                  }
                  @keyframes starGlow {
                    0%, 100% { opacity: 0.3; transform: scale(0.8); }
                    50%       { opacity: 1; transform: scale(1.2); }
                  }
                  .bob-body        { animation: bobChill 2.4s ease-in-out infinite; display: inline-block; }
                  .bob-head        { animation: bobHeadNod 2.4s ease-in-out infinite; transform-origin: 36px 20px; }
                  .bob-arm-left    { animation: bobArmLeft 2.4s ease-in-out infinite; transform-origin: 26px 42px; }
                  .bob-arm-right   { animation: bobArmRight 2.4s ease-in-out infinite; transform-origin: 46px 42px; }
                  .bob-dreads      { animation: dreadFlow 2.4s ease-in-out infinite; transform-origin: center top; }
                  .smoke-1         { animation: smokeRise 1.8s ease-out infinite; }
                  .smoke-2         { animation: smokeRise 1.8s ease-out infinite 0.6s; }
                  .smoke-3         { animation: smokeRise 1.8s ease-out infinite 1.2s; }
                  .note-1          { animation: noteFloat1 1.4s ease-out infinite; }
                  .note-2          { animation: noteFloat2 1.6s ease-out infinite 0.5s; }
                  .star-1          { animation: starGlow 1.2s ease-in-out infinite; }
                  .star-2          { animation: starGlow 1.2s ease-in-out infinite 0.4s; }
                  .star-3          { animation: starGlow 1.2s ease-in-out infinite 0.8s; }
                `}</style>
                <div className="bob-body">
                  <svg width="90" height="115" viewBox="0 0 90 115" fill="none" xmlns="http://www.w3.org/2000/svg">

                    {/* ── Floating music notes ── */}
                    <g className="note-1"><text x="68" y="18" fontSize="11" fill="#fbbf24">♪</text></g>
                    <g className="note-2"><text x="4"  y="24" fontSize="9"  fill="#86efac">♫</text></g>

                    {/* ── Stars / vibes ── */}
                    <g className="star-1"><text x="2"  y="12" fontSize="8" fill="#fde68a">★</text></g>
                    <g className="star-2"><text x="74" y="38" fontSize="7" fill="#6ee7b7">★</text></g>
                    <g className="star-3"><text x="0"  y="52" fontSize="6" fill="#fbbf24">✦</text></g>

                    {/* ── Dreads (behind head, flowing) ── */}
                    <g className="bob-dreads">
                      {/* Rasta hat / tam base */}
                      <ellipse cx="45" cy="10" rx="18" ry="8" fill="#1a0a00"/>
                      {/* Dreads hanging down */}
                      <path d="M27 14 Q20 28 18 46 Q21 50 25 46 Q24 32 30 18" fill="#2d1a00"/>
                      <path d="M30 15 Q24 30 22 50 Q25 54 28 50 Q27 36 33 20" fill="#3d2200"/>
                      <path d="M63 14 Q70 28 72 46 Q69 50 65 46 Q66 32 60 18" fill="#2d1a00"/>
                      <path d="M60 15 Q66 30 68 50 Q65 54 62 50 Q63 36 57 20" fill="#3d2200"/>
                      <path d="M45 14 Q44 32 42 54 Q45 57 48 54 Q46 32 45 14" fill="#2d1a00"/>
                    </g>

                    {/* ── Rasta tam hat ── */}
                    {/* Hat brim */}
                    <ellipse cx="45" cy="14" rx="17" ry="5" fill="#c8102e"/>
                    {/* Hat body - red, gold, green stripes */}
                    <path d="M28 14 Q28 4 45 2 Q62 4 62 14 Z" fill="#c8102e"/>
                    <path d="M30 11 Q32 5 45 3 Q58 5 60 11 Z" fill="#ffd700"/>
                    <path d="M33 8 Q36 4 45 3 Q54 4 57 8 Z" fill="#007a33"/>
                    {/* Hat top pom */}
                    <circle cx="45" cy="3" r="3" fill="#ffd700"/>

                    {/* ── Bob's head ── */}
                    <g className="bob-head">
                      <ellipse cx="45" cy="24" rx="13" ry="14" fill="#8B5E3C"/>
                      {/* Neck */}
                      <rect x="40" y="36" width="10" height="7" rx="2" fill="#8B5E3C"/>

                      {/* Eyes — half-closed / relaxed */}
                      <ellipse cx="39" cy="22" rx="3" ry="2" fill="#1a0a00"/>
                      <ellipse cx="51" cy="22" rx="3" ry="2" fill="#1a0a00"/>
                      {/* Sleepy eyelids */}
                      <path d="M36 21 Q39 19.5 42 21" stroke="#5c3317" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
                      <path d="M48 21 Q51 19.5 54 21" stroke="#5c3317" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
                      {/* Eye shine */}
                      <circle cx="40" cy="21.5" r="0.8" fill="white" opacity="0.7"/>
                      <circle cx="52" cy="21.5" r="0.8" fill="white" opacity="0.7"/>
                      {/* Eyebrows */}
                      <path d="M36 19 Q39 17.5 42 19" stroke="#1a0a00" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                      <path d="M48 19 Q51 17.5 54 19" stroke="#1a0a00" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                      {/* Relaxed smile */}
                      <path d="M38 29 Q45 34 52 29" stroke="#7c3a1e" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                      {/* Nose */}
                      <ellipse cx="45" cy="26" rx="2" ry="1.2" fill="#6b3a1f" opacity="0.5"/>
                      {/* Cheeks */}
                      <ellipse cx="35" cy="27" rx="4" ry="2.5" fill="#c2410c" opacity="0.2"/>
                      <ellipse cx="55" cy="27" rx="4" ry="2.5" fill="#c2410c" opacity="0.2"/>
                    </g>

                    {/* ── Body — relaxed open shirt (red/green/gold) ── */}
                    {/* Shirt */}
                    <rect x="32" y="42" width="26" height="24" rx="5" fill="#c8102e"/>
                    {/* Shirt open collar / chest */}
                    <path d="M45 42 L40 54 L45 52 L50 54 Z" fill="#8B5E3C"/>
                    {/* Shirt stripes */}
                    <rect x="32" y="50" width="26" height="4" fill="#ffd700" opacity="0.5"/>
                    <rect x="32" y="58" width="26" height="4" fill="#007a33" opacity="0.5"/>

                    {/* ── Left arm — relaxed, slightly raised ── */}
                    <g className="bob-arm-left" style={{ transformOrigin: '32px 44px' }}>
                      <rect x="20" y="42" width="12" height="18" rx="6" fill="#8B5E3C"/>
                      <circle cx="26" cy="62" r="5" fill="#8B5E3C"/>
                    </g>

                    {/* ── Right arm — holding a guitar-like pose ── */}
                    <g className="bob-arm-right" style={{ transformOrigin: '58px 44px' }}>
                      <rect x="58" y="42" width="12" height="18" rx="6" fill="#8B5E3C"/>
                      <circle cx="64" cy="62" r="5" fill="#8B5E3C"/>
                    </g>

                    {/* ── Pants — jeans / relaxed ── */}
                    <rect x="33" y="64" width="24" height="22" rx="3" fill="#3b5998"/>
                    {/* Belt */}
                    <rect x="33" y="64" width="24" height="4" rx="2" fill="#ffd700"/>
                    <rect x="43" y="63" width="4" height="6" rx="1" fill="#c8a000"/>

                    {/* ── Left leg ── */}
                    <rect x="33" y="84" width="10" height="18" rx="5" fill="#3b5998"/>
                    {/* Left shoe */}
                    <ellipse cx="38" cy="103" rx="8" ry="4" fill="#1a0a00"/>

                    {/* ── Right leg ── */}
                    <rect x="47" y="84" width="10" height="18" rx="5" fill="#3b5998"/>
                    {/* Right shoe */}
                    <ellipse cx="52" cy="103" rx="8" ry="4" fill="#1a0a00"/>

                    {/* ── Smoke wisps from vibe ── */}
                    <g className="smoke-1"><ellipse cx="22" cy="58" rx="3" ry="2" fill="#d1fae5" opacity="0.5"/></g>
                    <g className="smoke-2"><ellipse cx="24" cy="55" rx="2" ry="1.5" fill="#a7f3d0" opacity="0.4"/></g>
                    <g className="smoke-3"><ellipse cx="20" cy="52" rx="2.5" ry="1.5" fill="#6ee7b7" opacity="0.3"/></g>

                  </svg>
                </div>
                <span style={{ fontSize: '10px', color: '#86efac', letterSpacing: '1.5px', fontStyle: 'italic' }}>one love 🌿</span>
              </div>
            )}

            {/* Hidden YouTube iframe */}
            <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}>
              {isPlaying && (
                <iframe
                  ref={playerRef}
                  src="https://www.youtube.com/embed/vbED91NU4vE?autoplay=1&controls=0&loop=1&playlist=vbED91NU4vE"
                  allow="autoplay"
                  title="Background music player"
                  width="1"
                  height="1"
                />
              )}
            </div>
          </div>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 border rounded-full px-4 sm:px-5 py-1.5 text-[10px] tracking-[2.5px] sm:tracking-[3.5px] uppercase font-medium mb-6 text-[#E8A820]" style={{ borderColor: 'rgba(200,134,10,0.40)', background: 'rgba(200,134,10,0.08)' }}>
✨ Premium THC Edibles & Oils ✨
          </div>

          {/* H1 */}
          <h1 className="font-bebas leading-[0.92] tracking-[3px] mb-3" style={{ fontSize: 'clamp(60px,16vw,160px)' }}>
            <span className="block grad-purple-mid">Alchemy</span>
            <span className="block grad-gold" style={{ fontSize: '0.55em', letterSpacing: '8px' }}>Candy</span>
          </h1>

          {/* Tagline */}
          <p className="font-playfair italic text-text-muted mb-10" style={{ fontSize: 'clamp(14px,2.5vw,20px)' }}>
            It's all good man - 0% stress, 100% CHILL
          </p>

          {/* CTA */}
          <button
            onClick={() => scrollTo('gummies')}
            className="inline-flex items-center gap-2.5 text-white px-7 sm:px-8 py-3.5 rounded-full text-[13px] tracking-[2px] font-medium transition-all hover:-translate-y-0.5 hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #6B1A8A, #8B2AAA)', color: '#F0E8FF', boxShadow: '0 6px 30px rgba(107,26,138,0.55)', border: '1px solid rgba(232,192,64,0.25)' }}>
            
            <img src="/assets/gummy-bear.svg" alt="Gummies" className="w-5 h-5" /> Explore Our Menu
          </button>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 flex flex-col items-center gap-1.5 text-text-muted text-[10px] tracking-[3px] uppercase z-[1] animate-scroll-bob">
          MENU
          <span className="block w-px h-8" style={{ background: 'linear-gradient(to bottom, #C8860A, transparent)' }} />
        </div>
      </section>

      <div className="divider" />

      {/* ── Meet The Alchemist ── */}
      <section className="relative z-[1] max-w-[1180px] mx-auto px-4 sm:px-6 py-14 sm:py-24 fade-up">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Image */}
          <div className="relative flex-shrink-0 w-full max-w-[320px] lg:max-w-[380px] mx-auto lg:mx-0">
            <div className="absolute -inset-3 rounded-[28px] opacity-40 blur-2xl" style={{ background: 'radial-gradient(ellipse at 40% 60%, rgba(107,26,138,0.55), rgba(200,134,10,0.35))' }} />
            <div className="relative rounded-[24px] overflow-hidden shadow-[0_12px_60px_rgba(0,0,0,0.55)]" style={{ border: '1px solid rgba(200,134,10,0.30)' }}>
              <img
                src="/assets/images/walter-1783510166498.png"
                alt="The Alchemist — creator of Alchemy Candy edible oils and candies"
                className="w-full object-cover object-top"
                style={{ aspectRatio: '3/4', maxHeight: '480px' }} />
              
              {/* Subtle overlay badge */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] tracking-[2.5px] uppercase font-semibold whitespace-nowrap" style={{ background: 'rgba(14,8,24,0.80)', border: '1px solid rgba(200,134,10,0.45)', color: '#E8A820', backdropFilter: 'blur(8px)' }}>
                The Alchemist
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 border rounded-full px-4 py-1.5 text-[10px] tracking-[2.5px] uppercase font-medium mb-5 text-[#E8A820]" style={{ borderColor: 'rgba(200,134,10,0.35)', background: 'rgba(200,134,10,0.07)' }}>
              ✨ Our Story
            </div>
            <h2 className="font-bebas leading-[0.95] tracking-[3px] mb-5" style={{ fontSize: 'clamp(44px,9vw,88px)' }}>
              <span className="block grad-purple-mid">Meet</span>
              <span className="block grad-gold" style={{ fontSize: '0.85em', letterSpacing: '4px' }}>The Alchemist</span>
            </h2>
            <p className="font-playfair italic text-[clamp(16px,2.2vw,20px)] text-[#E8A820] mb-3 leading-snug">
              It's all good man!
            </p>
            <p className="font-bebas tracking-[3px] text-[clamp(18px,3vw,26px)] mb-5" style={{ color: '#C8860A' }}>
              0% stress, 100% Chill
            </p>
            <p className="text-text-muted text-[14px] sm:text-[15px] leading-[1.85] tracking-[0.3px] mb-6 max-w-[520px] mx-auto lg:mx-0">
              What started as a personal hobby has evolved into something much bigger — crafting premium candies &amp; oils that genuinely help people. Each batch is made with care and intention, designed to help you:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-[520px] mx-auto lg:mx-0 mb-8">
              {[
              { icon: '😌', label: 'Relax' },
              { icon: '😴', label: 'Sleep Well' },
              { icon: '😊', label: 'Better Mood' },
              { icon: '🧘', label: 'Less Anxiety' },
              { icon: '💆', label: 'Pain Relief' },
              { icon: '❤️', label: 'Made with Love' }].
              map((b) =>
              <div key={b.label} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[12px] tracking-[0.5px] font-medium text-text-muted" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <span className="text-lg">{b.icon}</span>
                  {b.label}
                </div>
              )}
            </div>
            <button
              onClick={() => scrollTo('gummies')}
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-[13px] tracking-[2px] font-medium transition-all hover:-translate-y-0.5 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #6B1A8A, #8B2AAA)', color: '#F0E8FF', boxShadow: '0 6px 30px rgba(107,26,138,0.50)', border: '1px solid rgba(232,192,64,0.25)' }}>
              
              <img src="/assets/gummy-bear.svg" alt="Gummies" className="w-4 h-4" /> Shop The Collection
            </button>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ── Delivery ── */}
      <section id="delivery" className="relative z-[1] max-w-[1180px] mx-auto px-4 sm:px-6 py-12 sm:py-20 fade-up">
        <SectionHeader emoji="📍📦" title="DELIVERY" sub="Pick your area · We drop off all over Nairobi" colorClass="c-gold" />
        <div className="max-w-[700px] mx-auto bg-card border border-[rgba(200,134,10,0.20)] rounded-[20px] p-5 sm:p-8 shadow-[0_4px_40px_rgba(139,58,0,0.25)]">
          <div className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 mb-4 text-[10px] tracking-[2.5px] uppercase font-medium bg-[rgba(200,134,10,0.10)] border border-[rgba(200,134,10,0.25)] text-[#E8A820]">
            Delivery Zone
          </div>
          <div className="font-bebas text-[clamp(22px,4vw,32px)] tracking-[2px] mb-1 c-gold">Which Area Are You In?</div>
          <p className="text-text-muted text-[13px] leading-[1.75] mb-6">
            Pick your neighbourhood from the list below. Delivery fees depend on your location and will be shared with you on WhatsApp before we send it out.
          </p>

          <div className="text-[10px] tracking-[3px] uppercase text-text-muted font-medium mb-3.5 flex items-center gap-2.5">
            Select Your Location
            <span className="flex-1 h-px bg-[rgba(240,232,255,0.08)]" />
          </div>

          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
            {NAIROBI_ZONES.map((zone) =>
            <button
              key={zone}
              className={`dloc ${selectedZone === zone ? 'sel-loc' : ''}`}
              onClick={() => setSelectedZone(zone)}>
              
                {zone}
              </button>
            )}
          </div>

          {selectedZone &&
          <div className="mt-6 p-4 rounded-xl bg-[rgba(107,58,26,0.10)] border border-[rgba(107,58,26,0.30)] text-[13px] text-[#C8860A]">
              <span className="inline-flex items-center gap-1.5">Dropping off in <strong>{selectedZone}</strong> — Hit us up on WhatsApp to lock in your delivery fee.</span>
            </div>
          }

          <p className="mt-5 text-[12px] text-text-muted leading-relaxed tracking-[0.3px]">
            ✅ Once you've selected your location, scroll down to browse our products and add them to your cart.
          </p>
        </div>

        {/* Opening Hours */}
        <div className="max-w-[700px] mx-auto mt-5 px-5 py-4 rounded-[16px] bg-card border border-[rgba(200,134,10,0.18)]">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">🕐</span>
            <div className="text-[10px] tracking-[2.5px] uppercase text-text-muted font-medium">Opening Hours</div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px] mb-3">
            <span className="text-text-muted">Monday</span>
            <span className="text-[#E8A820] font-medium">10:00 am – 6:00 pm</span>
            <span className="text-text-muted">Tuesday – Friday</span>
            <span className="text-[#E8A820] font-medium">10:00 am – 6:00 pm</span>
            <span className="text-text-muted">Saturday</span>
            <span className="text-[#E8A820] font-medium">10:30 am – 5:00 pm</span>
            <span className="text-text-muted">Sunday</span>
            <span className="text-red-400 font-medium">Closed</span>
          </div>
          <p className="text-[11px] text-text-muted leading-relaxed border-t border-[rgba(200,134,10,0.15)] pt-2">
            ⚠️ Orders placed after 6:00 pm will be processed the next morning.
          </p>
        </div>
      </section>

      <div className="divider" />

      {/* ── Products ── */}
      {PRODUCTS.map((product, idx) =>
      <React.Fragment key={product.id}>
          <section id={product.id} className="relative z-[1] max-w-[1180px] mx-auto px-4 sm:px-6 py-12 sm:py-20 fade-up">
            <SectionHeader
            emoji={product.emoji}
            title={product.subtitle}
            sub={product.id === 'gummies' ? `Strong (50mg) · ${product.flavors.length > 0 ? `${product.flavors.length} flavours · ` : ''}Made with love` : product.id === 'brownies' ? `2 potency levels · 1 flavour (low potency) · Made with love` : `${product.levels.length} ${product.levels.length === 1 ? 'potency level' : 'potency levels'} · ${product.flavors.length > 0 ? `${product.flavors.length} flavours · ` : ''}Made with love`}
            colorClass={colorMap[product.color].title} />

            {product.id === 'gummies' && (
              <p className="text-center text-text-muted text-[13px] leading-[1.75] max-w-[600px] mx-auto mt-4 sm:-mt-8 mb-10">
                RSO (Rick Simpson Oil) is a highly concentrated, full-spectrum cannabis extract typically containing 60–90% THC.
              </p>
            )}

            {product.id === 'gummies' && (
              <p className="text-center mb-8 -mt-6">
                <a
                  href="https://alchemycandy.live/forum/threads/rso-benefits-why-its-much-better-than-smoking.32/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E8A820] text-[13px] underline underline-offset-2 hover:text-[#f5c842] transition-colors"
                >
                  Click here to learn about all the benefits of using RSO.
                </a>
              </p>
            )}

            <ProductCard product={product} onAddToCart={addToCart} />
          </section>
          {idx < PRODUCTS.length - 1 && <div className="divider" />}
          {idx === 0 && (
            <>
              <div className="divider" />
            </>
          )}
        </React.Fragment>
      )}

      {/* ── Reviews ── */}
      <div id="reviews" className="relative z-[1] py-12 sm:py-20 px-4 sm:px-6 fade-up" style={{ background: 'linear-gradient(160deg, #221408 0%, #2A1A0A 100%)', borderTop: '1px solid rgba(200,134,10,0.20)', borderBottom: '1px solid rgba(200,134,10,0.20)' }}>
        <SectionHeader emoji="💬" title="WHAT THEY SAY" sub="* Real feedback from customers" colorClass="c-gold" />
        <div className="max-w-[1140px] mx-auto grid gap-4 sm:gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))' }}>
          {REVIEWS.map((r, i) =>
          <div key={i} className="review-card">
              <div className="relative overflow-hidden">
                <img src={r.img} alt={r.alt} className="w-full object-cover object-top" style={{ height: '360px' }} />
              </div>
            </div>
          )}
        </div>
        <p className="text-center mt-6 text-[13px] font-playfair" style={{ color: 'rgba(200,160,60,0.85)' }}>
          Want to view more feedback from customers?{' '}
          <a
            href="https://alchemycandy.live/forum/threads/whatsapp-customer-feedback-topic.33/#post-76"
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-semibold hover:opacity-80 transition-opacity"
            style={{ color: '#D4A017' }}>
            CLICK here
          </a>
          .
        </p>
        <p className="text-center mt-8 text-text-muted text-[13px] font-playfair italic tracking-[0.5px]">
          Every order matters to us — don't miss out, get yours delivered today ✨
        </p>

        {/* Community CTA removed */}
      </div>

      {/* ── Footer ── */}
      <footer className="relative z-[1] py-10 px-4 sm:px-6 text-center" style={{ background: 'var(--bg2)', borderTop: '1px solid rgba(212,160,23,0.20)' }}>
        <div className="inline-flex items-center gap-2 mb-3">
          <span className="font-bebas text-[clamp(22px,5vw,32px)] tracking-[4px] grad-purple-gold flex items-center gap-2">
            Alchemy Candy
          </span>
        </div>
        <div className="flex justify-center mb-5">
          <img
            src="/assets/images/image_9670ab64-1783854370697.png"
            alt="The Alchemist — Alchemy Candy logo"
            className="w-48 h-48 object-contain opacity-90"
          />
        </div>
        <div className="flex flex-col items-center gap-3 mb-4">
          <a
            href="https://www.instagram.com/alchemycandy"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] tracking-[1px] font-medium transition-all duration-200 hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)', color: '#fff' }}>
            
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
            Follow The Alchemist on Instagram
          </a>
          <a
            href="https://alchemycandy.live/forum"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-[12px] tracking-[1px] font-medium transition-all duration-200 hover:scale-105 border border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-black">
            Join the Community
          </a>
        </div>
        <p className="text-text-muted text-[12px] leading-[2] tracking-[0.5px]">
          <span className="text-[11px]">Full payment required on all orders · Prices in Kenyan Shillings (KSh)</span>
        </p>
      </footer>

      {/* ── Cart Drawer ── */}
      <CartDrawer open={cartOpen} items={cart} onClose={() => setCartOpen(false)} onRemove={removeFromCart} selectedZone={selectedZone} />

      {/* ── Toast ── */}
      <div className={`toast ${toastVisible ? 'show' : ''}`}>{toastMsg}</div>
    </div>);

}